import { Component, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { EXTENSION_CONTEXT, ExtensionContext, ON_SUBMIT_TOKEN } from '../extension-context';
import { SharedService } from '../../../core/services/shared.service';

/**
 * Extension FAC_DESGLOSE_PAGO — "Detalle de Pago por Transaccion" (tabla FAC27ARC).
 *
 * Reemplaza el mantenimiento legacy del desglose de pago montandolo como una
 * seccion inyectada sobre el LST de la transaccion (advancedtable), via SYS02EXT.
 *
 * Lee las llaves de la transaccion desde filtrosObligatorios$ (CNT05COD, FAC04COD
 * caja, FAC20COD grupo, FAC21COD consecutivo, CNT01COD moneda) y:
 *   - Carga los totales (Total a Cancelar / Pagado / Faltante / Vuelto) en COL y DOL.
 *   - Captura las 4 formas de pago (Efectivo / Tarjeta / Cheque / Deposito).
 *   - Graba cada forma de pago contra FAC27ARC_WS/Grabar (reutiliza el WS existente).
 *   - Termina la transaccion contra FAC27ARC_WS/Terminar.
 *
 * Catalogos del combo "Emisor TC / Banco CK / Cuenta DP":
 *   - Tarjeta (TC)  -> emisores de tarjeta (PAD10ARC) -> se manda en FAC08COD.
 *   - Cheque  (CK)  -> bancos emisores (BNC06ARC)     -> se manda en BNC06COD.
 *   - Deposito(DP)  -> cuentas bancarias (BNC04ARC)   -> se manda en BNC04COD.
 */

interface Totales {
  cancelarCOL: number;
  cancelarDOL: number;
  pagadoCOL: number;
  pagadoDOL: number;
  faltanteCOL: number;
  faltanteDOL: number;
  vueltoCOL: number;
  vueltoDOL: number;
}

interface Combo {
  cod: string;
  des: string;
}

interface FormaPago {
  tip: 'EF' | 'TC' | 'CK' | 'DP';
  label: string;
  monto: number;
  documento: string;        // FAC27NDO (voucher / num. cheque / num. deposito)
  fecha: string;            // FAC27FDO (solo CK / DP)
  emisor: string;           // codigo seleccionado en el combo
  usaDocumento: boolean;
  usaFecha: boolean;
  usaCombo: boolean;
}

@Component({
  selector: 'app-fac-desglose-pago',
  template: `
    <div class="card my-2 border-0 shadow-sm" *ngIf="tieneTransaccion; else sinTransaccion">

      <!-- ============ Seccion 1: Totales ============ -->
      <div class="card-body py-2">
        <table class="table table-sm table-borderless mb-2" style="max-width: 460px">
          <thead>
            <tr class="text-muted small">
              <th></th>
              <th class="text-end">Local</th>
              <th class="text-end">Funcional</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Total a Cancelar</strong></td>
              <td class="text-end">{{ tot.cancelarCOL | number:'1.2-2' }}</td>
              <td class="text-end">{{ tot.cancelarDOL | number:'1.2-2' }}</td>
            </tr>
            <tr>
              <td><strong>Total Pagado</strong></td>
              <td class="text-end text-success">{{ tot.pagadoCOL | number:'1.2-2' }}</td>
              <td class="text-end text-success">{{ tot.pagadoDOL | number:'1.2-2' }}</td>
            </tr>
            <tr>
              <td><strong>Faltante</strong></td>
              <td class="text-end" [class.text-danger]="tot.faltanteCOL > 0">{{ tot.faltanteCOL | number:'1.2-2' }}</td>
              <td class="text-end" [class.text-danger]="tot.faltanteCOL > 0">{{ tot.faltanteDOL | number:'1.2-2' }}</td>
            </tr>
            <tr>
              <td><strong>Vuelto</strong></td>
              <td class="text-end">{{ tot.vueltoCOL | number:'1.2-2' }}</td>
              <td class="text-end">{{ tot.vueltoDOL | number:'1.2-2' }}</td>
            </tr>
          </tbody>
        </table>

        <!-- ============ Seccion 2: Formas de pago ============ -->
        <div class="d-flex">
          <!-- Selector de moneda -->
          <div class="me-4">
            <div class="text-muted small mb-1"><strong>Moneda</strong></div>
            <div class="form-check" *ngFor="let m of monedas">
              <input class="form-check-input" type="radio" name="moneda"
                     [id]="'mon_' + m.cod" [value]="m.cod"
                     [(ngModel)]="moneda">
              <label class="form-check-label" [for]="'mon_' + m.cod">{{ m.des }}</label>
            </div>
          </div>

          <!-- Grilla de formas de pago -->
          <div class="flex-grow-1">
            <table class="table table-sm align-middle mb-0">
              <thead class="text-muted small">
                <tr>
                  <th style="min-width:110px">Forma Pago</th>
                  <th style="min-width:120px">Monto</th>
                  <th style="min-width:140px">Documento</th>
                  <th style="min-width:140px">Fecha</th>
                  <th style="min-width:220px">Emisor TC / Banco CK / Cuenta DP</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let f of formas">
                  <td><strong>{{ f.label }}</strong></td>
                  <td>
                    <input type="number" class="form-control form-control-sm text-end"
                           [(ngModel)]="f.monto" min="0" step="0.01">
                  </td>
                  <td>
                    <input type="text" class="form-control form-control-sm"
                           [(ngModel)]="f.documento" [disabled]="!f.usaDocumento || f.monto <= 0">
                  </td>
                  <td>
                    <input type="date" class="form-control form-control-sm"
                           [(ngModel)]="f.fecha" [disabled]="!f.usaFecha || f.monto <= 0">
                  </td>
                  <td>
                    <select class="form-control form-control-sm"
                            [(ngModel)]="f.emisor" [disabled]="!f.usaCombo || f.monto <= 0">
                      <option value="">-- Seleccione --</option>
                      <option *ngFor="let c of comboDe(f)" [value]="c.cod">{{ c.des }}</option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ============ Acciones ============ -->
        <div class="text-center mt-3">
          <button class="btn btn-sm btn-primary me-2" (click)="grabarDesglose()"
                  [disabled]="guardando || tot.faltanteCOL <= 0">
            <i class="fa fa-save me-1"></i> Grabar Desglose de Pago
          </button>
        </div>

        <div *ngIf="mensaje" class="alert mt-2 py-1 px-2 mb-0" [ngClass]="mensajeOk ? 'alert-success' : 'alert-danger'">
          {{ mensaje }}
        </div>
      </div>
    </div>

    <ng-template #sinTransaccion>
      <div class="text-muted small my-2">
        <i class="fa fa-info-circle me-1"></i> Seleccione una transaccion para registrar su desglose de pago.
      </div>
    </ng-template>
  `
})
export class FacDesglosePagoComponent implements OnInit, OnDestroy {

  formas: FormaPago[] = [
    { tip: 'EF', label: 'Efectivo', monto: 0, documento: '', fecha: '', emisor: '', usaDocumento: false, usaFecha: false, usaCombo: false },
    { tip: 'TC', label: 'Tarjeta',  monto: 0, documento: '', fecha: '', emisor: '', usaDocumento: true,  usaFecha: false, usaCombo: true  },
    { tip: 'CK', label: 'Cheque',   monto: 0, documento: '', fecha: '', emisor: '', usaDocumento: true,  usaFecha: true,  usaCombo: true  },
    { tip: 'DP', label: 'Deposito', monto: 0, documento: '', fecha: '', emisor: '', usaDocumento: true,  usaFecha: true,  usaCombo: true  }
  ];

  tot: Totales = { 
    cancelarCOL: 0, cancelarDOL: 0, 
    pagadoCOL : 0, pagadoDOL : 0,
    faltanteCOL : 0, faltanteDOL : 0,
    vueltoCOL : 0, vueltoDOL : 0
  };

  emisoresTC: Combo[] = [];
  bancosCK:   Combo[] = [];
  cuentasDP:  Combo[] = [];
  monedas: Combo[] = [];

  moneda = null;
  guardando = false;
  mensaje = '';
  mensajeOk = false;
  tieneTransaccion = false;

  private llaves: any = null;     // llaves de la transaccion (CNT05COD, FAC04COD, FAC20COD, FAC21COD, CNT01COD)
  private destroy$ = new Subject<void>();
  private onSubmit = inject(ON_SUBMIT_TOKEN);

  constructor(
    @Inject(EXTENSION_CONTEXT) private ctx: ExtensionContext,
    private dataService: SharedService
  ) {}

  ngOnInit() {
    // Las llaves de la transaccion llegan como filtros obligatorios del LST.
    this.ctx.filtrosObligatorios$
      .pipe(startWith([] as any[]), takeUntil(this.destroy$))
      .subscribe(llaves => {
        const k = Array.isArray(llaves) && llaves.length ? llaves[0] : null;
        if (k && k.FAC21COD) {
          this.llaves = k;
          this.tieneTransaccion = true;
          this.cargarTotales();
          this.cargarCatalogos();
        }
      });

    // Recargar totales cuando el LST se refresca.
    this.ctx.reload$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.tieneTransaccion) this.cargarTotales();
    });

  }

  // -------- Carga de datos --------

  private cargarTotales() {
    const body = JSON.stringify({ LLAVES: [this.llaves] });
    this.dataService.ConsultarWS('api/ws/fac/FAC27ARC_WS/Totales/' + this.ctx.SYS02CON, body)
      .subscribe({
        next: (resp: any) => {
          this.tot = {
            cancelarCOL: resp?.cancelarCOL || 0,
            cancelarDOL: resp?.cancelarDOL || 0,
            pagadoCOL: resp?.pagadoCOL || 0,
            pagadoDOL: resp?.pagadoDOL || 0,
            faltanteCOL: resp?.faltanteCOL || 0,
            faltanteDOL: resp?.faltanteDOL || 0,
            vueltoCOL: resp?.vueltoCOL || 0,
            vueltoDOL: resp?.vueltoDOL || 0,
          };
          this.moneda = resp?.monedaDef;
        },
        error: (e) => this.error('No se pudieron cargar los totales.', e)
      });
  }

  private cargarCatalogos() {
    const body = JSON.stringify({ LLAVES: [this.llaves] });
    this.dataService.ConsultarWS('api/ws/fac/FAC27ARC_WS/Catalogos/' + this.ctx.SYS02CON, body)
      .subscribe({
        next: (resp: any) => {
          this.emisoresTC = resp?.emisoresTC || [];
          this.bancosCK   = resp?.bancosCK   || [];
          this.cuentasDP  = resp?.cuentasDP  || [];
          this.monedas  =   resp?.monedas    || [];
        },
        error: (e) => this.error('No se pudieron cargar los catalogos.', e)
      });
  }

  comboDe(f: FormaPago): Combo[] {
    switch (f.tip) {
      case 'TC': return this.emisoresTC;
      case 'CK': return this.bancosCK;
      case 'DP': return this.cuentasDP;
      default:   return [];
    }
  }

  // -------- Acciones --------

  grabarDesglose() {
    const lineas = this.formas.filter(f => (+f.monto || 0) > 0);
    if (!lineas.length) { this.error('Ingrese al menos un monto.'); return; }

    // Validacion espejo de la del WS: documento obligatorio salvo efectivo.
    for (const f of lineas) {
      if (f.tip !== 'EF' && !f.documento.trim()) {
        this.error(`Ingrese el documento de la forma de pago "${f.label}".`);
        return;
      }
    }

    this.guardando = true;
    this.mensaje = '';

    // Una llamada Grabar por forma de pago (igual al contrato actual del WS).
    const peticiones = lineas.map(f => {
      const llave = {
        ...this.llaves,
        FAC27TIP: f.tip,
        CNT01COD: this.moneda,
        FAC27MON: f.monto,
        FAC27NDO: f.tip === 'EF' ? '' : f.documento,
        FAC27FDO: f.usaFecha ? f.fecha : '',
        FAC08COD: f.tip === 'TC' ? f.emisor : '',
        BNC06COD: f.tip === 'CK' ? f.emisor : '',
        BNC04COD: f.tip === 'DP' ? f.emisor : ''
      };
      const body = JSON.stringify({ LLAVES: [llave] });
      return this.dataService.ConsultarWS('api/ws/fac/FAC27ARC_WS/Grabar/' + this.ctx.SYS02CON, body).toPromise();
    });

    Promise.all(peticiones)
      .then(() => {
        this.ok('Desglose de pago grabado.');
        this.onSubmit("Recargar pagos");
      })
      .catch(e => this.error('No se pudo grabar el desglose.', e))
      .finally(() => this.guardando = false);
  }

  // -------- Mensajes --------

  private ok(msg: string)  { this.mensaje = msg; this.mensajeOk = true; }
  private error(msg: string, e?: any) {
    this.mensaje = e?.error?.message || e?.message || msg;
    this.mensajeOk = false;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }


}
